/* Location API methods almost entirely uses the old approach of passing
 state as the first positional param of each of its standalone methods.
 This setup of `LocationApi extends EntityApi` is for new methods.
 See https://field.atlassian.net/wiki/spaces/FIELD/pages/430309377/RFC+FS+Repository+API+Adapter+Pattern
*/
const { EntityApi } = require('../common')
const { wrapEntityApi } = require('../utils/wrap-api')
const PGAdapter = require('./../common/pg-adapter')
const lodashGet = require('lodash/get')
// Legacy methods requiring `state` as first param
const {
  getByIds,
  getByWarehouseCodes,
  get,
  getByWarehouseCode,
  getOrFakeName,
  getWithProposal,
  listWithProposals,
  proposeChange,
  decideProposal,
  listAll,
  listAllRaw,
  listRelated,
  listChildren,
  exportLocations,
  importLocations,
  saveLocations,
  listAdminLocations,
  getBySignupUid,
  updateContactPhone
} = require('./api')
const { PgGraph } = require('./graph/pg-graph')
const tools = require('./tools')
const { resolveReadSource } = require('../tools/resolve-read-source')
const { pgRowsToLocations } = require('./tools/pg-rows-to-locations')

const rawMethods = {
  get,
  getOrFakeName,
  getByIds,
  getByWarehouseCodes,
  getByWarehouseCode,
  listAllRaw,
  listAll,
  listRelated,
  listChildren,
  proposal: {
    getWithProposal,
    listWithProposals,
    proposeChange,
    decideProposal
  },
  exportLocations,
  importLocations,
  saveLocations,
  tools,
  listAdminLocations,
  getBySignupUid,
  updateContactPhone
}

// Same raw methods, but with `get` renamed so it doesn't shadow the
// dispatching `get` class method once wrapped and assigned onto `this` below.
const { get: legacyGet, ...unchangedRawMethods } = rawMethods
const legacyMethods = { ...unchangedRawMethods, getLegacy: legacyGet }

const LocationPouchAdapter = require('./data-access/pouchdb-adapter/location-pouch-adapter')
const LocationRestAdapter = require('./data-access/rest-adapter')
const LocationOrdersStatusHistoryAdapter = require('./data-access/orders-status-history-rest-adapter')
const { LocationPGAdapter } = require('./location-pg-adapter')
const LocationRelationshipPgAdapter = require('./data-access/location-relationship-pg-adapter')
const LocationRelationshipRulePgAdapter = require('./data-access/location-relationship-rule-pg-adapter')
const LocationOptionsPgAdapter = require('./data-access/location-options-pg-adapter')
const LocationServicePgAdapter = require('./data-access/location-service-pg-adapter')
const LocationServiceContactPgAdapter = require('./data-access/location-service-contact-pg-adapter')

class LocationApi extends EntityApi {
  constructor (state, restAdapter, logger, pgConnection, agaveAdapter) {
    const { user, locationsDB } = state
    const adapter = new LocationPouchAdapter(
      user, locationsDB
    )
    super(adapter)

    this.state = state
    this.restAdapter = new LocationRestAdapter(restAdapter)
    this.agaveAdapter = agaveAdapter

    // TODO: remove this when all raw methods have been ported
    const apiMethods = wrapEntityApi(legacyMethods, state)
    Object.assign(this, apiMethods)

    // `getByIds` is the raw pouch read — only `get` consulted the data-source
    // resolver — so for an online-only user, who keeps no local
    // integrated-data pouch, it answered [] rather than failing. Branching here
    // serves every caller, instead of each one having to reach for
    // `getLocationsViaIds` itself.
    //
    // `date` is forwarded rather than dropped: the endpoint hydrates with it
    // (see `getLocationsViaIds`), so a caller asking as of a report date — the
    // forecast screen does — gets that period over agave too.
    //
    // Wrapped rather than declared as a class method because the Object.assign
    // above copies the raw method onto the instance, which would shadow one.
    const getByIdsFromPouch = this.getByIds
    this.getByIds = (locationIds, date, options) => (
      this.readSource({ options }) === 'agave'
        ? this.getLocationsViaCrud(locationIds, date)
        : getByIdsFromPouch(locationIds, date, options)
    )

    if (pgConnection) {
      this.HAS_PG_ACCESS = true
      const { user = {} } = state
      const username = user.name
      this.pgAdapter = new LocationPGAdapter({pgConnection, username, logger})
      this.supplyGraphPgAdapter = new LocationSupplyGraphPgAdapter({pgConnection, username, logger})
      this.locationRelationshipPgAdapter = new LocationRelationshipPgAdapter({pgConnection, username, logger})
      this.locationRelationshipRulePgAdapter = new LocationRelationshipRulePgAdapter({pgConnection, username, logger})
      this.locationOptionsPgAdapter = new LocationOptionsPgAdapter({pgConnection, username, logger})
      this.locationServicePgAdapter = new LocationServicePgAdapter({pgConnection, username, logger})
      this.locationServiceContactPgAdapter = new LocationServiceContactPgAdapter({pgConnection, username, logger})
      this.ordersStatusHistory = new LocationOrdersStatusHistoryAdapter({
        pgConnection, username, logger
      })
      this.pgGraph = new PgGraph(pgConnection)
    }
  }

  listAdditionalFields () {
    return this.adapter.listAdditionalFields()
  }

  // Two-way dispatch for the by-id reads — `get` and `getByIds`: 'agave' for an
  // online-only browser, 'pouch' for everyone else, and for agave's own fs-api,
  // which has no transport, so the handler serving
  // `location/get_raw_doc/by_ids` answers from couch rather than calling back
  // out to itself. No 'pg' branch here: the pg reads belong to the agave
  // controllers, which use `this.pgAdapter` directly.
  //
  // Both reads resolve to 'agave', but they do not go to the same place once
  // they are there: `getByIds` takes the crud/pg channel, `get` stays on
  // `get_raw_doc/by_ids`. See `get` for why.
  //
  // `options` is what neither channel can honour — `{ raw: true }` — so an
  // options read stays on pouch, where it is implemented. Only `getByIds` takes
  // options; `get` has none to pass. A `date` is not passed here either: it does
  // not change where the read goes, only what the read hydrates with.
  readSource ({ options } = {}) {
    return resolveReadSource({
      domain: 'location',
      state: this.state,
      hasAgaveTransport: !!this.agaveAdapter,
      mustReadLocally: options !== undefined
    })
  }

  // Browser-side dispatch: online-only users keep no local pouch, so a single
  // location has to be resolved over HTTP. Despite its name,
  // `location/get_raw_doc/by_ids` does not return raw docs — it calls
  // `location.getByIds` server-side, which hydrates exactly like `getLegacy`
  // does (see api/lib/location/api/read/get-by-ids.js), so the shape returned
  // here is the same on both branches.
  //
  // Deliberately *not* the crud/pg channel `getByIds` reads over. The two look
  // like the same read at different arities, but their callers are not the same
  // set. `getByIds` is reached for by name, and every one of those callers was
  // checked to read identity and `additionalData` only. `get` is the general
  // single-location read — some forty call sites — and two of them read the
  // `services` that `pgRowsToLocations` answers `[]` for:
  //
  // - `statements/StatementsContainer`, which takes
  //   `programs.0.services.0.id` as the service to load the period and report
  //   for when it was not passed one. That is the PSM delivery statement
  //   (shipments/.../psm-delivery-note, routed as
  //   last-mile-deliveries/delivery-statement) — a PSM-app read, not a
  //   shelflife one — and it passes `isWithLedger: false`, so the failed
  //   lookups are swallowed and the statement degrades silently.
  // - `shipments/complete/CompleteContainer`, which takes
  //   `destinationLocation.services[0]` as the service to translate product
  //   aliases for; `translateTerritoryAlias` throws outright on a falsy
  //   serviceId.
  //
  // So `get` waits for the crud read to join `data_location_service`, which is
  // the work the next online-only user roles need anyway.
  async get (locationId, date, includeProgramsHistory = false) {
    if (this.readSource() === 'agave') {
      if (includeProgramsHistory) {
        // `getByIds` — and so the agave endpoint — has no programsHistory path.
        // Fail loudly rather than quietly returning a location without it.
        throw new Error('LocationApi.get: includeProgramsHistory is not supported over agave')
      }
      const [location] = await this.getLocationsViaIds([locationId], false, date)
      return location
    }
    return this.getLegacy(locationId, date, includeProgramsHistory)
  }

  // Read locations over the agave crud route, which answers them from Postgres:
  // `data_location` with its `data_location_options` columns joined on, shaped
  // back into location entities by `pgRowsToLocations`.
  //
  // This serves the online-only `getByIds`, and only that. It is deliberately
  // not wired into `getLocationsViaIds`, which some twenty callers reach for
  // explicitly — shelflife exports, package notes, order suppliers — and which
  // goes over `get_raw_doc/by_ids` for all of them whether they are online-only
  // or not, nor into `get`. That endpoint hydrates from couch and so can answer
  // for programs and services; this one cannot (see `pgRowsToLocations`), so
  // repointing either would quietly drop that data for their callers.
  //
  // `fsid` is sent as a repeated query param, which the crud controller
  // recognises as a by-ids read; `limit` is belt and braces, so that if that
  // fast path ever stops matching, the read is not silently capped at the paged
  // default of 50.
  async getLocationsViaCrud (locationIds = [], date) {
    if (!locationIds.length) {
      return []
    }

    const { results } = await this.agaveAdapter.get('location', {
      fsid: locationIds,
      limit: locationIds.length
    })

    return pgRowsToLocations(results, { date })
  }

  getLocationsViaIds (locationIds, dryRun, date) {
    if (dryRun) {
      return []
    } else if (this.agaveAdapter) {
      return this.agaveAdapter.create('location/get_raw_doc/by_ids', { locationIds, date })
    }
    // Fallback to pouch
    return this.mainApi.location.getByIds(locationIds, date)
  }

  getMarketData (alias) {
    if (this.agaveAdapter) {
      return this.agaveAdapter.create(`location/market_data${alias ? `?alias=${alias}` : ''}`)
    }
    return this.pgAdapter.getMarketData(alias)
  }

  async getLocationBlockingRule (locationId) {
    if (this.pgAdapter) {
      const data = await this.pgAdapter.getLocationBlockingRule(locationId)
      const blockingRule = lodashGet(data, 'blocking_rule', 'default')
      return { blockingRule }
    }
    return this.agaveAdapter.get(`location/blocking_rule?locationId=${locationId}`)
  }
}

class LocationSupplyGraphPgAdapter extends PGAdapter {
  constructor ({pgConnection, username, logger}) {
    const columns = [
      'id',
      'supply_from_id',
      'supply_to_id',
      'updated_by'
    ]

    const opts = {addUuid: true}

    super(pgConnection, 'avocado.data_locationsupply', tools.BACKEND_SERVICE_USERNAME, columns, 'id', {}, logger, opts)
  }
}

module.exports = rawMethods
module.exports.LocationApi = LocationApi
